import { ArrayUtil } from "@coten-inc/utils";

import type { DataSourceFormData } from "~/features/dataImportProjects/types";
import type { RelationCategoryInstance, SchemaDesignerCategoryInstance, SheetState } from "./schemaDesigner";
import { isRelationInstance } from "./sheetPlanner";
import { type SheetVariantKeys, VariantKeySlots } from "./variantKeySlots";

export type BootstrapDataImportProjectV2DataSourceSchemaMappingInput =
  | {
      type: "PROPERTIES";
      categoryId: string;
      categoryPropertyName: string;
      dataSourcePropertyLabel: string;
      variantKey?: string;
    }
  | {
      type: "RELATION_SOURCE" | "RELATION_TARGET";
      categoryId: string;
      fkResolver: {
        query: {
          evidenceMetadata: {
            path: string[];
            equals: string;
          };
        };
        fkConstraint?: {
          linkedCategoryName: string;
          linkedInstanceLabel: string;
          fkColumnLabel: string;
        };
      };
    };

const MAPPING_TYPES = {
  PROPERTIES: "PROPERTIES",
  RELATION_SOURCE: "RELATION_SOURCE",
  RELATION_TARGET: "RELATION_TARGET",
} as const;

function buildPropertyMappings(
  sheet: SheetState,
  variantKeys?: SheetVariantKeys,
): BootstrapDataImportProjectV2DataSourceSchemaMappingInput[] {
  const variantKeyByInstance = VariantKeySlots.applicableVariantKeys(sheet, variantKeys);

  return sheet.columns
    .map((column) => {
      // Skip system columns (FK references like target1._UUID)
      if (column.columnKind === "foreignKey") {
        return;
      }

      // if the column is a custom column, use the category id of the sheet
      const categoryId = column.categoryId ? column.categoryId : sheet.category.category.id;
      const variantKey = variantKeyByInstance.get(column.categoryInstanceId ?? sheet.category.instanceId);

      const mapping: BootstrapDataImportProjectV2DataSourceSchemaMappingInput = {
        type: MAPPING_TYPES.PROPERTIES,
        categoryId,
        categoryPropertyName: column.property.name, // Here we send the category property name, not the dataSource property name (= column label)
        dataSourcePropertyLabel: column.columnLabel,
        ...(variantKey ? { variantKey } : {}),
      };

      return mapping;
    })
    .filter(ArrayUtil.notEmptyPredicate);
}

function getInstanceLabel(instance: SchemaDesignerCategoryInstance) {
  return instance.instanceLabel ?? instance.category.name;
}

function getRelevantRelationsForSheet(
  sheet: SheetState,
  relationInstances: RelationCategoryInstance[],
): RelationCategoryInstance[] {
  if (sheet.category.category.type === "relation") {
    return [sheet.category as RelationCategoryInstance];
  }

  const sheetInstanceId = sheet.category.instanceId;

  return relationInstances.filter((relation) => {
    const sourceInstanceId = relation.sourceCategory?.instanceId;
    const targetInstanceId = relation.targetCategory?.instanceId;

    return sourceInstanceId === sheetInstanceId || targetInstanceId === sheetInstanceId;
  });
}

function buildRelationMappings(
  sheet: SheetState,
  relationInstances: RelationCategoryInstance[],
): BootstrapDataImportProjectV2DataSourceSchemaMappingInput[] {
  const fkColumns = sheet.columns.filter((column) => column.columnKind === "foreignKey");

  // skip the sheet if it doesn't have any foreign key columns
  if (fkColumns.length === 0) {
    return [];
  }

  const relevantRelations = getRelevantRelationsForSheet(sheet, relationInstances);

  if (relevantRelations.length === 0) {
    return [];
  }

  return relevantRelations.flatMap((relation) => {
    const pairs = [
      { type: MAPPING_TYPES.RELATION_SOURCE, entityCategory: relation.sourceCategory },
      { type: MAPPING_TYPES.RELATION_TARGET, entityCategory: relation.targetCategory },
    ];

    return pairs.map(({ type, entityCategory }) => {
      let resolvedColumn;
      if (entityCategory) {
        const label = getInstanceLabel(entityCategory);
        const matchingColumns = fkColumns.filter((column) => column.fkRef.sheetName === label);
        resolvedColumn =
          matchingColumns.find((column) => column.columnLabel.startsWith(`${relation.instanceLabel}.`)) ??
          matchingColumns[0];
      }

      return {
        type,
        categoryId: relation.category.id,
        fkResolver: {
          query: {
            evidenceMetadata: {
              path: ["biz.trace.source_id"],
              // As this resolver is a part of RelationComposer,
              // we need to use the (category) property name, instead of the column label.
              equals: resolvedColumn
                ? `{{properties.${resolvedColumn.columnLabel}}}`
                : "{{metadata.biz.trace.source_id}}",
            },
          },
          fkConstraint:
            resolvedColumn && entityCategory
              ? {
                  linkedCategoryName: entityCategory.category.name,
                  linkedInstanceLabel: getInstanceLabel(entityCategory),
                  fkColumnLabel: resolvedColumn.columnLabel,
                }
              : undefined,
        },
      };
    });
  });
}

function toBootstrapDataImportProjectV2SchemaMappings(
  formData: DataSourceFormData,
): BootstrapDataImportProjectV2DataSourceSchemaMappingInput[][] {
  const relationInstances = formData.sheets.map((sheet) => sheet.category).filter(isRelationInstance);

  return formData.sheets.map((sheet) => {
    const schemaMappings = [
      ...buildPropertyMappings(sheet, formData.variantKeys),
      // Add relation mappings for both entity sheets and relation sheets
      ...buildRelationMappings(sheet, relationInstances),
    ];
    return schemaMappings;
  });
}

export const JobSchemaMapper = {
  toBootstrapDataImportProjectV2SchemaMappings,
};
